import {action, Action, Thunk, thunk} from 'easy-peasy';
import {
    Archive,
    ArchivePayload,
    ArchiveTree,
    DocumentationTabs,
    Equipment,
    MachineSpecification
} from '~/types';
import {API} from "~/app/routes";
import {CommonModel, getCommonModel} from "~/store/commonStore";
import axios from "axios";
import {StoreModel} from "~/store";
import FileTransferService from "~/sqlite/services/fileTransfer";
import { documentationRepository } from '~/data/repositories/DocumentationRepository';
import { getFileNameFromUrl } from "~/user/helpers";

export interface ArchiveTreePayload {
    data: Archive[],
    tree: ArchiveTree[],
    treeMap: number[],
    parentIndex: number
}

export interface DownloadProgressPayload {
    id: Archive['id'],
    progress: number | undefined
}

export interface DocumentationModel extends CommonModel<MachineSpecification> {
    tabs: DocumentationTabs['activeTab'][]
    activeTab: DocumentationTabs['activeTab']
    setActiveTab: Action<DocumentationModel, DocumentationTabs['activeTab']>

    machineSpecifications: MachineSpecification
    machineSpecificationsHiddenInformation: string[]
    fetchMachineSpecifications: Thunk<DocumentationModel, MachineSpecification['id'], any, StoreModel, Promise<void>>
    fetchedMachineSpecifications: Action<DocumentationModel, MachineSpecification>

    machineEquipments: Equipment[]
    fetchMachineEquipments: Thunk<DocumentationModel, Equipment['id'], any, StoreModel, Promise<void>>
    fetchedMachineEquipments: Action<DocumentationModel, Equipment[]>

    fetchFavoriteMachinesSpecifications: Thunk<DocumentationModel, void, any, StoreModel, Promise<void>>
    fetchedFavoriteMachinesSpecifications: Thunk<DocumentationModel, MachineSpecification[], any, StoreModel, Promise<void>>

    machineArchives: Archive[]
    fetchMachineArchives: Thunk<DocumentationModel, ArchivePayload, any, StoreModel, Promise<void>>
    fetchedMachineArchives: Thunk<DocumentationModel, Archive[], any, StoreModel>
    prepareMachineArchives: Thunk<DocumentationModel, Archive[], any, StoreModel>
    setMachineArchives: Action<DocumentationModel, Archive[]>

    machineParentArchive: Archive | undefined
    setMachineParentArchive: Action<DocumentationModel, Archive | undefined>

    machineArchivesTree: ArchiveTree[]
    currentTreeMap: number[]
    setCurrentTreeMap: Action<DocumentationModel, Archive['id']>
    prepareMachineArchivesTree: Thunk<DocumentationModel, ArchiveTreePayload, any, StoreModel>
    setMachineArchivesTree: Action<DocumentationModel, ArchiveTree[]>

    parentFolderId: number
    setParentFolderId: Action<DocumentationModel, number>


    openArchiveFile: Thunk<DocumentationModel, {archive: Archive, machineId: string}, any, StoreModel>

    downloadProgress: number[]
    setDownloadProgress: Action<DocumentationModel, DownloadProgressPayload>
}

export const documentationModel: DocumentationModel = {
    ...getCommonModel<MachineSpecification>(API.machines),

    tabs: ['specifications', 'equipments', 'archives'],
    activeTab: 'specifications',
    setActiveTab: action((state, payload) => {
        state.activeTab = payload;
    }),

    machineSpecifications: {
        id: '',
        name: '',
        intern: '',
        regNo: ''
    },
    machineSpecificationsHiddenInformation: ['id', 'intern'],
    fetchMachineSpecifications: thunk(async (actions, payload) => {
        const data = await documentationRepository.loadSpecifications(payload);
        data && actions.fetchedMachineSpecifications(data);
    }),
    fetchedMachineSpecifications: action((state, payload) => {
        state.machineSpecifications = payload;
    }),

    machineEquipments: [],
    fetchMachineEquipments: thunk(async (actions, payload) => {
        const data = await documentationRepository.loadEquipments(payload);
        actions.fetchedMachineEquipments(data);
    }),
    fetchedMachineEquipments: action((state, payload) => {
        state.machineEquipments = payload;
    }),

    fetchFavoriteMachinesSpecifications: thunk(async (actions) => {
        const data = await documentationRepository.fetchAndCacheFavoriteSpecifications();
        await actions.fetchedFavoriteMachinesSpecifications(data);
    }),
    fetchedFavoriteMachinesSpecifications: thunk(async (actions, payload) => {
        await documentationRepository.cacheSpecifications(payload);
    }),

    machineArchives: [],
    fetchMachineArchives: thunk(async (actions, payload) => {
        const data = await documentationRepository.loadArchives(payload);
        await actions.fetchedMachineArchives(data);
    }),
    fetchedMachineArchives: thunk((actions, data, helpers) => {
        const {parentFolderId} = helpers.getState();
        actions.setCurrentTreeMap(parentFolderId);

        const {machineArchivesTree, currentTreeMap} = helpers.getState();
        actions.prepareMachineArchivesTree({
            data,
            tree: machineArchivesTree,
            treeMap: currentTreeMap,
            parentIndex: 0
        });
        actions.prepareMachineArchives(data);
    }),
    prepareMachineArchives: thunk(async (actions, data) => {
        const fileTransfer = new FileTransferService();
        const archives = await Promise.all(data.map(async (item): Promise<Archive> => {
            let filePath = item.type === 1 && item.fileType !== 'link' && item.downloadLink && await fileTransfer.isFileExist(item.downloadLink, 'archives');
            !!filePath && actions.setDownloadProgress({id: item.id, progress: 100});
            return item;
        }));
        actions.setMachineArchives(archives);
    }),
    setMachineArchives: action((state, payload) => {
        state.machineArchives = payload;
    }),
    machineParentArchive: undefined,
    setMachineParentArchive: action((state, payload) => {
        state.machineParentArchive = payload
    }),

    machineArchivesTree: [],
    currentTreeMap: [],
    setCurrentTreeMap: action((state, payload) => {
        if (payload) {
            let index = state.currentTreeMap.findIndex((i) => i === payload);

            if (index === -1) {
                state.currentTreeMap = [...state.currentTreeMap, payload];
            } else {
                state.currentTreeMap.length = index + 1;
            }
        } else {
            state.currentTreeMap = [];
        }
    }),
    prepareMachineArchivesTree: thunk((actions, payload, helpers) => {
        let {data, tree, treeMap, parentIndex} = payload;
        let {parentFolderId} = helpers.getState();
        if (parentFolderId === 0) {
            tree = data;
            actions.setMachineParentArchive(undefined);
        } else {
            if (typeof tree !== 'undefined') {
                let index = tree.findIndex((entry) => entry.id === treeMap[parentIndex]);

                if (treeMap[parentIndex] === parentFolderId) {
                    actions.setMachineParentArchive(tree[index]);
                    tree[index].child = data;
                } else {
                    if (typeof tree[index].child !== 'undefined') {
                        parentIndex++;
                        actions.prepareMachineArchivesTree({
                            data,
                            tree: tree[index].child || [],
                            treeMap,
                            parentIndex
                        });
                    }
                }
            }
        }
        actions.setMachineArchivesTree(tree);
    }),
    setMachineArchivesTree: action((state, payload) => {
        state.machineArchivesTree = payload
    }),

    parentFolderId: 0,
    setParentFolderId: action((state, payload) => {
        state.parentFolderId = payload;
    }),

    openArchiveFile: thunk(async (actions, payload) => {
        const {archive: {id, downloadLink, mimeType}, machineId} = payload;
        const fileTransfer = new FileTransferService();
        const documentsOfflineService = documentationRepository;

        if (window['cordova']) {
            const filePath = await fileTransfer.isFileExist(downloadLink, `archives/${machineId}`);
            if(filePath) {
                try {
                    await documentsOfflineService.updateDownloadedDocument(id.toString(), `archives/${machineId}/${getFileNameFromUrl(downloadLink)}`)
                } catch (e) {
                    console.error("Failed to update downloaded document status:", e);
                }
                fileTransfer.openFile(filePath, mimeType)
            } else {
                actions.setDownloadProgress({id, progress: -1});
                axios.create({
                    withCredentials: false,
                    headers: {
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
                    }
                }).get(downloadLink, {
                    responseType: 'blob',
                    onDownloadProgress: progressEvent => {
                        let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        actions.setDownloadProgress({id, progress: percentCompleted});
                    }
                }).then(async response => {
                    fileTransfer.saveFileFromBlobResponse(response, downloadLink, `archives/${machineId}`).then(async (path) => {
                        try {
                            await documentsOfflineService.updateDownloadedDocument(id.toString(), `archives/${machineId}/${getFileNameFromUrl(downloadLink)}`);
                        } catch (e) {
                            console.error("Failed to update downloaded document status:", e);
                        }
                        //fileTransfer.openFile(path, mimeType)
                    });
                }).catch(() => {
                    actions.setDownloadProgress({id, progress: undefined});
                });
            }
        } else {
            fileTransfer.openFile(downloadLink, mimeType)
        }

    }),

    downloadProgress: [],
    setDownloadProgress: action((state, payload) => {
        if(payload.progress !== undefined) {
            state.downloadProgress[payload.id] = payload.progress;
        } else {
            state.downloadProgress.splice(payload.id, 1);
        }
    })
};
